import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { useTheme } from '@mui/material/styles';

/**
 * The three states every data-backed screen needs and the legacy apps mostly
 * skipped: empty, loading, error. Having one implementation each is what keeps
 * "no results" from looking like a broken page in one module and a blank card
 * in another.
 */

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
  /** `inline` for inside a card, `page` for a whole route. */
  variant?: 'inline' | 'page';
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  variant = 'inline',
}: EmptyStateProps) {
  const theme = useTheme();
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap={1.5}
      sx={{
        textAlign: 'center',
        py: variant === 'page' ? 10 : 6,
        px: 3,
        color: 'text.secondary',
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: `${theme.custom.radius.lg}px`,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          {icon}
        </Box>
      )}
      <Box>
        <Typography variant="h4" color="text.primary" gutterBottom>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ maxWidth: 420, mx: 'auto' }}>
            {description}
          </Typography>
        )}
      </Box>
      {action && (
        <Button variant="contained" size="small" onClick={action.onClick} sx={{ mt: 0.5 }}>
          {action.label}
        </Button>
      )}
    </Stack>
  );
}

export interface ErrorStateProps {
  title?: string;
  /** Keep this the operator-facing message; log the raw error separately. */
  message?: string;
  onRetry?: () => void;
  variant?: 'inline' | 'page';
}

export function ErrorState({
  title = 'Something went wrong',
  message = 'The request could not be completed. Try again, and if it keeps happening contact IT support.',
  onRetry,
  variant = 'inline',
}: ErrorStateProps) {
  return (
    <Box sx={{ py: variant === 'page' ? 6 : 2, px: variant === 'page' ? 3 : 0 }}>
      <Alert
        severity="error"
        action={
          onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : undefined
        }
      >
        <AlertTitle>{title}</AlertTitle>
        {message}
      </Alert>
    </Box>
  );
}

export interface LoadingStateProps {
  /** `spinner` for short waits, `skeleton` for table/list placeholders. */
  variant?: 'spinner' | 'skeleton';
  rows?: number;
  label?: string;
}

export function LoadingState({
  variant = 'spinner',
  rows = 5,
  label = 'Loading…',
}: LoadingStateProps) {
  if (variant === 'skeleton') {
    return (
      <Stack gap={1} sx={{ py: 1 }} aria-busy="true" aria-label={label}>
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={44} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" justifyContent="center" gap={1.5} sx={{ py: 6 }} aria-busy="true">
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}
